import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useTable } from '@refinedev/antd'
import type { HttpError } from '@refinedev/core'
import { Breadcrumb, Button, Space, Table } from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import {
  BULK_TERMINATE_CONFIRM_TEXT,
  BULK_TERMINATE_DESCRIPTION,
  TerminateConfirmModal,
} from '@/components/common/TerminateConfirmModal'
import { VM_TERMINAL_STATUSES } from '@/constants/statuses'
import {
  labelsForIds,
  useResourceTermination,
} from '@/hooks/useResourceTermination'
import { useTableRowNavigation } from '@/hooks/useTableRowNavigation'
import { UsersCacheProvider } from '@/hooks/useUsersByIds'
import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'
import { searchByNameOrId } from '@/utils/searchFilters'

import { vmColumns } from './columns'
import { ComputeFilterForm } from './components/ComputeFilterForm'
import { DeployVmModal } from './components/DeployVmModal'

type UserVmDto = components['schemas']['AdminUserVmDto']

interface ComputeFilters {
  search?: string
  status?: string
  userId?: string
  clusterId?: string
}

export default function ComputeListPage() {
  const {
    selectedRowKeys,
    setSelectedRowKeys,
    terminateTarget,
    setTerminateTarget,
    doTerminate,
    bulkTerminateOpen,
    openBulkTerminate,
    closeBulkTerminate,
  } = useResourceTermination<UserVmDto>({
    resourceLabel: 'VM',
    terminateFn: (ids) =>
      client.POST('/v1/admin/vms/terminate', { body: { vmIds: ids } }),
  })
  const onRow = useTableRowNavigation('vms')
  const [deployOpen, setDeployOpen] = useState(false)

  const { tableProps, searchFormProps, filters } = useTable<
    UserVmDto,
    HttpError,
    ComputeFilters
  >({
    resource: 'vms',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
    onSearch: (values) => [
      ...searchByNameOrId(values.search, 'ids', true),
      { field: 'status', operator: 'eq', value: values.status || undefined },
      { field: 'userId', operator: 'eq', value: values.userId || undefined },
      {
        field: 'clusterId',
        operator: 'eq',
        value: values.clusterId || undefined,
      },
    ],
  })

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }} items={[{ title: 'Compute' }]} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Space>
          {selectedRowKeys.length > 0 && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={openBulkTerminate}
            >
              Terminate ({selectedRowKeys.length})
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setDeployOpen(true)}
          >
            Deploy VM
          </Button>
        </Space>
      </div>

      <ComputeFilterForm searchFormProps={searchFormProps} filters={filters} />

      <UsersCacheProvider
        ids={(tableProps.dataSource ?? []).map((vm) => vm.userId)}
      >
        <Table
          {...tableProps}
          pagination={tablePaginationConfig(tableProps)}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys as string[]),
            getCheckboxProps: (record: UserVmDto) => ({
              disabled: VM_TERMINAL_STATUSES.has(record.status),
            }),
          }}
          onRow={onRow}
          columns={vmColumns(setTerminateTarget)}
          scroll={{ x: 800 }}
        />
      </UsersCacheProvider>

      <DeployVmModal open={deployOpen} onClose={() => setDeployOpen(false)} />

      <TerminateConfirmModal
        open={terminateTarget !== null}
        title="Terminate VM"
        resourceName={terminateTarget?.name ?? ''}
        onConfirm={() => doTerminate([terminateTarget?.id ?? ''])}
        onCancel={() => setTerminateTarget(null)}
      />

      <TerminateConfirmModal
        open={bulkTerminateOpen}
        title={`Terminate ${selectedRowKeys.length} VM(s)`}
        targets={labelsForIds(selectedRowKeys, tableProps.dataSource)}
        confirmText={BULK_TERMINATE_CONFIRM_TEXT}
        description={BULK_TERMINATE_DESCRIPTION}
        onConfirm={() => doTerminate(selectedRowKeys)}
        onCancel={closeBulkTerminate}
      />
    </div>
  )
}
